import type { HalfTimeEffect } from '../../../data/full/babacool';
import { applyEnemyStatus } from '../../enemy-status';
import { keepWhere, touches, wholeTicks } from '../../effects';
import type { Upgraded } from '../../attacks/types';
import type { SkillModule } from '../../skills/types';
import type { PlayerState, PropState, SimState, Vec2 } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { ownerOf } from '../../props';

export const HALF_TIME = 'halfTime';

// Long enough that a bad vibe in the Half-time stays in it from one tick to the next.
const HOLD_TICKS = 2;

export const halfTime: SkillModule<HalfTimeEffect> = {
  cast({ state, tempo }, player, effect, _input, power) {
    if (state.props !== undefined) {
      keepWhere(state.props, (prop) => prop.kind !== HALF_TIME || prop.playerId !== player.id);
    }
    (state.props ??= []).push({
      id: state.nextEntityId,
      kind: HALF_TIME,
      playerId: player.id,
      phase: 'held',
      x: player.x,
      y: player.y,
      prevX: player.x,
      prevY: player.y,
      vx: 0,
      vy: 0,
      damage: 0,
      ticksLeft: Math.max(1, wholeTicks(effect.bars * tempo.ticksPerBar * power)),
      hitsLeft: 0,
    });
    state.nextEntityId += 1;
  },

  update(ctx) {
    const { state } = ctx;
    const props = state.props;
    if (props === undefined) {
      return;
    }
    let ended = false;
    for (const prop of props) {
      if (prop.kind !== HALF_TIME) {
        continue;
      }
      const owner = ownerOf(state, prop);
      prop.x = owner.x;
      prop.y = owner.y;
      prop.ticksLeft -= 1;
      ended = prop.ticksLeft <= 0 || ended;
      const reach = halfTimeReach(owner, halfTimeEffect(ctx, owner));
      for (const enemy of state.enemies) {
        if (enemy.hp > 0 && touches(enemy, prop, reach)) {
          applyEnemyStatus(state, enemy, 'halfTime', HOLD_TICKS);
        }
      }
    }
    if (ended) {
      keepWhere(props, (prop) => prop.kind !== HALF_TIME || prop.ticksLeft > 0);
      if (props.length === 0) {
        delete state.props;
      }
    }
  },
};

function halfTimeEffect(ctx: Pick<StepContext, 'content'>, owner: PlayerState): HalfTimeEffect {
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'halfTime') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no Half-time`);
  }
  return effect;
}

export function halfTimeReach(owner: Upgraded, effect: HalfTimeEffect): number {
  return statValue(owner, 'skillRadiusMul', effect.radius);
}

export function inHalfTime(
  ctx: Pick<StepContext, 'content' | 'state'>,
  owner: PlayerState,
  at: Vec2,
): boolean {
  return halfTimeOf(ctx.state, owner).some((prop) => {
    const reach = halfTimeReach(owner, halfTimeEffect(ctx, owner));
    const dx = at.x - prop.x;
    const dy = at.y - prop.y;
    return dx * dx + dy * dy <= reach * reach;
  });
}

function halfTimeOf(state: SimState, owner: PlayerState): PropState[] {
  return (state.props ?? []).filter(
    (prop) => prop.kind === HALF_TIME && prop.playerId === owner.id,
  );
}
